import { WORKFLOW_FILTER_TYPE, WORKFLOW_ACTION_TYPE } from '../../../api/automation-workflow-types';
import type { WorkflowFilter as ApiWorkflowFilter, WorkflowAction as ApiWorkflowAction } from '../../../api/automation-workflow-types';
import type { WorkflowFilterDraft, WorkflowActionDraft } from '../../types.d';
import type { FilterRegistry, ActionRegistry } from '../../registries/registry-types.d';
import { filterTypeHandlers } from './filter-type-handlers';
import { actionTypeHandlers } from './action-type-handlers';

// Converts UI filter drafts to API WorkflowFilter payloads.
// Drafts are grouped by their filterType (from the registry) and dispatched to
// the correct handler. New filter types only require adding a handler to
// filter-type-handlers.ts and a filterType field to the registry entry.
export const convertToApiFilters = (drafts: WorkflowFilterDraft[], registry: FilterRegistry): ApiWorkflowFilter[] => {
  if (drafts.length === 0) {
    return [];
  }

  const draftsByFilterType = drafts.reduce<Record<string, WorkflowFilterDraft[]>>((acc, draft) => {
    const entry = registry[draft.fieldName];
    const filterType = entry?.filterType ?? WORKFLOW_FILTER_TYPE.filterCriteria;
    return {
      ...acc,
      [filterType]: [...(acc[filterType] ?? []), draft],
    };
  }, {});

  return Object.entries(draftsByFilterType).reduce<ApiWorkflowFilter[]>((acc, [filterType, typedDrafts]) => {
    const handler = filterTypeHandlers[filterType];
    if (!handler) {
      return acc;
    }
    const definition = handler.toApi(typedDrafts, registry);
    if (!definition) {
      return acc;
    }
    acc.push({ type: filterType as ApiWorkflowFilter['type'], definition });
    return acc;
  }, []);
};

// Converts a UI action draft to an API WorkflowAction payload.
// Dispatches to the correct handler based on the action's type (from the registry).
// New action types only require adding a handler to action-type-handlers.ts and
// an actionType field to the registry entry.
export const convertToApiAction = (draft: WorkflowActionDraft, registry: ActionRegistry): ApiWorkflowAction => {
  const entry = registry[draft.actionTypeId];
  const actionType = entry?.actionType ?? WORKFLOW_ACTION_TYPE.orderRuleAction;
  const handler = actionTypeHandlers[actionType];
  if (!handler) {
    return {
      type: WORKFLOW_ACTION_TYPE.orderRuleAction,
      definition: {
        actionTypeId: draft.actionTypeId,
        parameter: draft.parameter,
        intParameter: draft.intParameter,
      },
    };
  }
  return {
    type: actionType as ApiWorkflowAction['type'],
    definition: handler.toApi(draft),
  };
};
